/**
 * Server-clock estimation from four-timestamp exchanges.
 *
 * The server's timestamps (`RoomSnapshot.server_now_ms`, every `*_server_ms`
 * field of `PlaybackState`, and `ClockPong`) are milliseconds on the server's
 * *monotonic* clock since process startup. They are not wall-clock time and
 * must never be compared against `Date.now()`. The client's counterpart is
 * `performance.now()`, also monotonic.
 *
 * For one exchange the client sends `client_send`, the server answers with
 * `server_receive`/`server_send`, and the client records `client_receive`:
 *
 *   offset = ((server_receive - client_send) + (server_send - client_receive)) / 2
 *   rtt    = (client_receive - client_send) - (server_send - server_receive)
 *
 * `offset` converts a client monotonic instant to the server's clock
 * (`server ≈ client + offset`); it therefore also carries the difference
 * between the two process start times and is meaningless outside this
 * connection's lifetime. Sign convention matches docs/protocol.md.
 *
 * A single sample's error is bounded by the path asymmetry, so a bounded
 * window of recent samples is kept and only the lowest-RTT ones are used
 * (queueing delay is one-sided and inflates a sample's error). Samples are
 * discarded on reconnect and after a suspend/resume, where the server may have
 * restarted or the monotonic clocks may have diverged.
 *
 * This module is deliberately DOM-free and takes every timestamp as an
 * argument so the arithmetic is testable without a browser.
 */

/** One accepted round trip. */
export interface ClockSample {
  /** `server ≈ client + offsetMs`. */
  readonly offsetMs: number;
  readonly rttMs: number;
  /** Client monotonic time at which the sample completed. */
  readonly clientMs: number;
}

/** How the current offset was obtained. */
export type ClockQuality =
  /** No round trip yet: one-way estimate from a snapshot's `server_now_ms`. */
  | 'bootstrap'
  /** At least one accepted `ClockPong` round trip. */
  | 'sampled';

export interface ClockEstimate {
  readonly offsetMs: number;
  readonly quality: ClockQuality;
  /** Best (lowest) RTT in the retained window; `null` for a bootstrap estimate. */
  readonly rttMs: number | null;
  /** Number of retained round-trip samples. */
  readonly samples: number;
  /**
   * Half the best RTT: the residual one-way uncertainty that the two-sample
   * formula cannot remove. Reported in the UI and in measurements.
   */
  readonly uncertaintyMs: number | null;
}

export const CLOCK_TUNING = {
  /** Samples retained; older ones are dropped even if their RTT was good. */
  windowSize: 12,
  /** Lowest-RTT samples actually used for the offset estimate. */
  bestSamples: 5,
  /** Above this RTT a sample is queueing noise, not a measurement. */
  maxRttMs: 2000,
  /** Sample age after which a retained sample is dropped. */
  maxSampleAgeMs: 120_000,
  /** Burst of pings right after connect/resync, to converge quickly. */
  burstCount: 5,
  burstIntervalMs: 250,
  /** Steady-state ping cadence once the window is populated. */
  steadyIntervalMs: 5000,
  /**
   * Wall-clock vs. monotonic divergence that means the machine slept; the
   * estimate is discarded because the server clock kept running.
   */
  suspendDetectionMs: 2000,
} as const;

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? 0;
  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}

export class ClockEstimator {
  #samples: ClockSample[] = [];
  /** Outstanding ping stamps, so an unsolicited or duplicated pong is ignored. */
  #pending = new Set<number>();
  #bootstrapOffsetMs: number | null = null;

  /**
   * Registers a ping about to be sent and returns its `client_send_ms`. The
   * value is a whole number because the wire field is a `u64`.
   */
  startPing(clientNowMs: number): number {
    const stamp = Math.round(clientNowMs);
    // A stamp can repeat if two pings are issued inside the same millisecond;
    // the set keeps it a single outstanding entry, which is correct.
    this.#pending.add(stamp);
    if (this.#pending.size > CLOCK_TUNING.windowSize * 2) {
      const oldest = this.#pending.values().next();
      if (!oldest.done) this.#pending.delete(oldest.value);
    }
    return stamp;
  }

  /**
   * Accepts one `ClockPong`. Returns the sample, or `null` when the exchange
   * was not solicited, was already consumed, or is too delayed/inconsistent to
   * be a measurement.
   */
  acceptPong(
    clientSendMs: number,
    serverReceiveMs: number,
    serverSendMs: number,
    clientReceiveMs: number,
  ): ClockSample | null {
    if (!this.#pending.delete(clientSendMs)) return null;

    const serverProcessingMs = serverSendMs - serverReceiveMs;
    const roundTripMs = clientReceiveMs - clientSendMs;
    const rttMs = roundTripMs - serverProcessingMs;
    if (!Number.isFinite(rttMs) || rttMs < 0 || rttMs > CLOCK_TUNING.maxRttMs) return null;
    if (serverProcessingMs < 0) return null;

    const offsetMs =
      (serverReceiveMs - clientSendMs + (serverSendMs - clientReceiveMs)) / 2;
    if (!Number.isFinite(offsetMs)) return null;

    const sample: ClockSample = { offsetMs, rttMs, clientMs: clientReceiveMs };
    this.#samples.push(sample);
    this.#prune(clientReceiveMs);
    return sample;
  }

  /**
   * Coarse offset from a snapshot's `server_now_ms`, used only until the first
   * round trip lands. It absorbs the whole one-way latency, so it is replaced
   * as soon as a real sample exists.
   */
  bootstrap(serverNowMs: number, clientReceiveMs: number): void {
    this.#bootstrapOffsetMs = serverNowMs - clientReceiveMs;
  }

  /** Drops every sample and the bootstrap offset (reconnect, suspend, epoch change). */
  reset(): void {
    this.#samples = [];
    this.#pending.clear();
    this.#bootstrapOffsetMs = null;
  }

  /** `null` until either a snapshot or a round trip has been seen. */
  estimate(clientNowMs: number): ClockEstimate | null {
    this.#prune(clientNowMs);
    if (this.#samples.length === 0) {
      if (this.#bootstrapOffsetMs === null) return null;
      return {
        offsetMs: this.#bootstrapOffsetMs,
        quality: 'bootstrap',
        rttMs: null,
        samples: 0,
        uncertaintyMs: null,
      };
    }

    const byRtt = [...this.#samples].sort((a, b) => a.rttMs - b.rttMs);
    const best = byRtt.slice(0, CLOCK_TUNING.bestSamples);
    const bestRtt = best[0]?.rttMs ?? null;
    return {
      offsetMs: median(best.map((sample) => sample.offsetMs)),
      quality: 'sampled',
      rttMs: bestRtt,
      samples: this.#samples.length,
      uncertaintyMs: bestRtt === null ? null : bestRtt / 2,
    };
  }

  /** Estimated server clock for a client monotonic instant, or `null`. */
  serverNowMs(clientNowMs: number): number | null {
    const estimate = this.estimate(clientNowMs);
    return estimate === null ? null : clientNowMs + estimate.offsetMs;
  }

  /** Interval until the next ping: burst while converging, steady afterwards. */
  nextPingDelayMs(): number {
    return this.#samples.length < CLOCK_TUNING.burstCount
      ? CLOCK_TUNING.burstIntervalMs
      : CLOCK_TUNING.steadyIntervalMs;
  }

  #prune(clientNowMs: number): void {
    const cutoff = clientNowMs - CLOCK_TUNING.maxSampleAgeMs;
    if (this.#samples.some((sample) => sample.clientMs < cutoff)) {
      this.#samples = this.#samples.filter((sample) => sample.clientMs >= cutoff);
    }
    if (this.#samples.length > CLOCK_TUNING.windowSize) {
      this.#samples = this.#samples.slice(this.#samples.length - CLOCK_TUNING.windowSize);
    }
  }
}

/**
 * Detects a suspend/resume (or a large clock jump) by comparing how far the
 * monotonic and wall clocks advanced between two observations. Used only to
 * decide that clock samples must be re-taken.
 */
export class SuspendDetector {
  #monotonicMs: number;
  #wallMs: number;

  constructor(monotonicMs: number, wallMs: number) {
    this.#monotonicMs = monotonicMs;
    this.#wallMs = wallMs;
  }

  /** True when the two clocks disagree by more than the tolerance since the last check. */
  check(monotonicMs: number, wallMs: number): boolean {
    const monotonicDelta = monotonicMs - this.#monotonicMs;
    const wallDelta = wallMs - this.#wallMs;
    this.#monotonicMs = monotonicMs;
    this.#wallMs = wallMs;
    return Math.abs(wallDelta - monotonicDelta) > CLOCK_TUNING.suspendDetectionMs;
  }
}
